import { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { compact, debounce, uniqBy } from 'lodash';
import { useRecoilValue } from 'recoil';

import type { ItemId, PrivateAssetSearchModeEnum } from 'venn-api';
import type { LibraryItemType } from 'venn-utils';
import { globalSearch, searchAnalysisView } from 'venn-api';
import {
  analyticsService,
  getCreatedByTrackingField,
  getDefaultInvestmentSearchResults,
  useApi,
  useCallIfMounted,
  useHasFF,
} from 'venn-utils';
import { proxySetIdState } from 'venn-state';

import type { Placeholders, SearchFilter, SearchMenuItem } from './types';
import type { HelpCenterId } from './search';
import { search, searchHelp } from './search';

import { getItemTrackingId, getItemType } from './components/shared';
import useTagsDropDown from './useTagsDropDown';
import { isSavedViews, viewFilters, isHelpArticles } from './components/QuickFilterBar';
import { getExcludedAnalysisTypes } from './getExcludedAnalysisTypes';
import { UserContext } from '../contexts';

export const useSearchMenu = ({
  onSelected,
  location,
  selectedFilters,
  menuIsOpen,
  excludedItems,
  investmentsOnly,
  portfoliosOnly,
  selectedValue,
  showRecentlyAnalyzed,
  proxyable,
  privateAssetSearchMode,
  initialQuery = '',
  placeholders,
  includeTags = true,
  shouldResetSearchOnSelect,
  pinSelected = true,
  maxResults,
  displayResultsAsBlock,
  itemTypes,
}: {
  onSelected: (result: SearchMenuItem) => void;
  location: string;
  selectedFilters: SearchFilter[];
  menuIsOpen: boolean;
  excludedItems?: ItemId[];
  investmentsOnly?: boolean;
  portfoliosOnly?: boolean;
  selectedValue?: string;
  showRecentlyAnalyzed?: boolean;
  privateAssetSearchMode: PrivateAssetSearchModeEnum;
  proxyable?: boolean;
  initialQuery?: string;
  placeholders?: Placeholders;
  includeTags?: boolean;
  shouldResetSearchOnSelect: boolean;
  pinSelected?: boolean;
  maxResults: number;
  displayResultsAsBlock?: boolean;
  itemTypes?: LibraryItemType[];
}) => {
  const [selected, setSelected] = useState<SearchMenuItem | null>(null);
  const [query, setQuery] = useState(initialQuery);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchMenuItem[]>([]);
  const [totalResults, setTotalResults] = useState<number>();
  const searchApi = useApi(globalSearch);
  const getDefaultInvestments = useApi(getDefaultInvestmentSearchResults);
  const { hasPermission } = useContext(UserContext);
  const isFirstSearchRef = useRef(true);
  const proxySetId = useRecoilValue(proxySetIdState);
  const helpCenterId: HelpCenterId = useHasFF('venn_help_on_riskpro_ff') ? 'intercom' : 'salesforce';

  /* When new results are loaded. Propagate them to the onSearchResults callback */
  const onResults = useCallIfMounted(
    useCallback((searchMenuItems: SearchMenuItem[], newTotalResults?: number) => {
      setResults(searchMenuItems);
      setTotalResults(newTotalResults);
    }, []),
  );

  const safeSetLoading = useCallIfMounted(setLoading);

  const debouncedSearchRef = useRef(
    debounce(async (...args: Parameters<typeof search>) => {
      await search(...args);
    }, 200),
  );

  const debouncedSearchHelpRef = useRef(
    debounce(async (...args: Parameters<typeof searchHelp>) => {
      await searchHelp(...args);
    }, 500),
  );

  const onSearch = useCallback((newSearch: string) => {
    setQuery(newSearch);
  }, []);

  const onSelect = useCallback(
    (result: SearchMenuItem) => {
      setSelected(result);
      onSelected(result);
      if (shouldResetSearchOnSelect) {
        setQuery('');
      }
      trackSelectionMade(result, results, location, query, selectedFilters, totalResults);
    },
    [setSelected, onSelected, shouldResetSearchOnSelect, results, location, query, selectedFilters, totalResults],
  );

  const { onTagsDropdown, onTagsClose, selectedTagDropdowns, openedTags, setOpenedTags, onCloseAllTags } =
    useTagsDropDown({
      results,
      onResults,
    });

  const refetchSearch = useCallback(async () => {
    const showViews = isSavedViews(selectedFilters);
    if (showViews) {
      const { content } = await searchAnalysisView({
        pageSize: maxResults,
        name: query ?? '',
        page: 1,
        excludedAnalysisViewTypes: getExcludedAnalysisTypes(hasPermission),
        includeAllInWorkspace: !selectedFilters.includes(viewFilters.SAVED_BY_ME!),
        vennTemplate: selectedFilters.includes(viewFilters.VENN_PROVIDED!),
      });
      onResults(
        content.results.map((item) => ({ viewInfo: item, category: 'view', label: item.name })),
        content.totalResults,
      );

      return;
    }

    if (isHelpArticles(selectedFilters)) {
      const isFirstSearch = isFirstSearchRef.current;
      isFirstSearchRef.current = false;
      (isFirstSearch ? searchHelp : debouncedSearchHelpRef.current)?.(query, onResults, helpCenterId);
      return;
    }

    // We only show the default search if there is no query and filters selected
    const defaultSearch = !query && !selectedFilters.length;
    const showDefaultSearch = showRecentlyAnalyzed || !!placeholders;
    // If we are not showing default results, reset the results to empty
    if (defaultSearch && !showDefaultSearch) {
      onResults([]);
    }

    const isFirstSearch = isFirstSearchRef.current;
    isFirstSearchRef.current = false;
    (isFirstSearch ? search : debouncedSearchRef.current)?.(
      defaultSearch ? '' : query,
      onResults,
      searchApi,
      getDefaultInvestments,
      safeSetLoading,
      location,
      selectedFilters,
      setOpenedTags,
      privateAssetSearchMode,
      maxResults,
      proxySetId,
      excludedItems,
      investmentsOnly,
      portfoliosOnly,
      defaultSearch,
      showDefaultSearch,
      proxyable,
      placeholders,
      includeTags,
      itemTypes,
    );
  }, [
    selectedFilters,
    query,
    showRecentlyAnalyzed,
    onResults,
    searchApi,
    getDefaultInvestments,
    safeSetLoading,
    location,
    setOpenedTags,
    privateAssetSearchMode,
    excludedItems,
    investmentsOnly,
    portfoliosOnly,
    proxyable,
    placeholders,
    hasPermission,
    includeTags,
    maxResults,
    proxySetId,
    itemTypes,
    helpCenterId,
  ]);

  useEffect(() => {
    if (menuIsOpen || displayResultsAsBlock) {
      refetchSearch();
    }
  }, [refetchSearch, selectedValue, menuIsOpen, displayResultsAsBlock]);

  const items =
    pinSelected && selected
      ? uniqBy([selected, ...results], (item) => {
          if (item.value) {
            return item.value.id;
          }
          return item.label;
        })
      : results;

  return {
    query,
    items,
    totalResults,
    loading,
    onSearch,
    onSelect,
    onTagsDropdown,
    onTagsClose,
    selectedTagDropdowns,
    openedTags,
    onCloseAllTags,
  };
};

const trackSelectionMade = (
  result: SearchMenuItem,
  searchMenuItems: SearchMenuItem[],
  location: string,
  query: string,
  selectedFilters: SearchFilter[],
  totalResults = 0,
) => {
  const searchResult = result.searchResult;
  const rank = searchMenuItems.findIndex(
    (item) =>
      searchResult?.fundId === item.searchResult?.fundId &&
      searchResult?.portfolioId === item.searchResult?.portfolioId,
  );
  const selectedItemTypes = compact(selectedFilters.map((filter) => filter.itemType));
  const selectedQuickFilters = compact(selectedFilters.map((filter) => filter.quickFilter));
  analyticsService.searchSelectionMade({
    location,
    totalResults,
    visibleResults: searchMenuItems.length,
    objectId: getItemTrackingId(result),
    objectType: getItemType(result),
    query,
    queryDriven: !!query,
    createdBy: searchResult ? getCreatedByTrackingField(searchResult) : '',
    rank,
    quickFilters: selectedQuickFilters,
    typeFilters: selectedItemTypes,
  });
};
